// Seguimiento de secuencia + reenvío del hub `/hub/tpv` (sin React, para
// poder probarlo). Lo usa useTpvRealtime.
//
//  - En directo: `accept(env)` → seqTracker (orden, duplicados, huecos).
//    Un hueco de >300 ms pide `GET realtime/sync?since=`; si llega otro
//    hueco mientras tanto, se encola UNA repetición (antes se perdía).
//  - Al volver (`catchUp()`, lo llama el orquestador de vuelta):
//    `GET realtime/seq` → nada nuevo ("none"), reenvío por los MISMOS
//    handlers ("replay") o estado completo ("snapshot": el servidor ya no
//    tiene el hueco, se reinició el API o la secuencia retrocedió).
//  - `beacon()` (cada 30 s): si el servidor va por delante sin que nos
//    llegara el evento, mismo reenvío que un hueco; epoch nuevo → resync.
//  - Todo evento con seq aplicado (directo o repuesto) se ofrece también a
//    `onApplied` (puente legacy, que deduplica por seq).
import { createSeqTracker } from "./seqTracker";

export function createRealtimeSync({
  fetchSeq,
  fetchSync,
  onApply,
  onApplied,
  requestSnapshot,
  record,
  trackerOptions = {},
} = {}) {
  let disposed = false;
  let catchingUp = false;
  let snapshotNeeded = false;
  // Un resync pedido fuera de un catch-up (hueco imposible de reponer en
  // directo) se recuerda hasta el siguiente catch-up: así la vuelta que lo
  // atiende hace snapshot aunque `realtime/seq` ya no muestre diferencia.
  let snapshotPending = false;

  const tracker = createSeqTracker({
    ...trackerOptions,
    onApply: (env) => {
      onApply?.(env);
      if (typeof env?.seq === "number" && env.seq > 0) onApplied?.(env);
    },
    onGap: (since) => {
      record?.("gap", since);
      liveSync(since);
    },
    onResync: (reason) => {
      record?.("resync", reason);
      if (catchingUp) {
        snapshotNeeded = true;
        return;
      }
      snapshotPending = true;
      requestSnapshot?.(reason);
    },
  });

  // Reenvíos serializados: uno a la vez + UNA repetición encolada (que
  // pide desde el lastSeq que haya en ese momento).
  let current = null;
  let next = null;
  const runSync = async (fallbackSince) => {
    const { lastSeq, epoch } = tracker.getState();
    const data = await fetchSync(lastSeq ?? fallbackSince, epoch);
    if (!disposed) tracker.applySync(data);
  };
  const doSync = (since) => {
    if (!current) {
      current = runSync(since).finally(() => {
        current = null;
      });
      return current;
    }
    if (!next) {
      next = current
        .catch(() => {})
        .then(() => {
          next = null;
          return doSync(since);
        });
    }
    return next;
  };

  const liveSync = (since) =>
    doSync(since).catch(() => {
      if (disposed) return;
      snapshotPending = true;
      requestSnapshot?.("sync-failed");
    });

  return {
    accept: (env) => tracker.accept(env),
    getState: () => tracker.getState(),

    /**
     * Beacon del servidor `{ epoch, latestSeq, ts }` (cada 30 s, sin sobre):
     *  - epoch distinto        → "resync" (API reiniciada: la vuelta hace estado completo).
     *  - latestSeq > lastSeq   → "catchup": mismo camino que un hueco (`realtime/sync?since=`).
     *  - latestSeq < lastSeq   → "resync" (la secuencia retrocedió).
     *  - igual / sin base      → "none" (sin punto de partida lo cubre el catch-up al conectar).
     */
    beacon(b) {
      if (disposed || !b) return "none";
      const { lastSeq, epoch, buffered } = tracker.getState();
      if (lastSeq == null) return "none";
      if (epoch != null && b.epoch != null && b.epoch !== epoch) {
        record?.("resync", "beacon-epoch");
        snapshotPending = true;
        requestSnapshot?.("beacon-epoch");
        return "resync";
      }
      if (typeof b.latestSeq !== "number") return "none";
      if (b.latestSeq < lastSeq) {
        record?.("resync", "beacon-seq-back");
        snapshotPending = true;
        requestSnapshot?.("beacon-seq-back");
        return "resync";
      }
      if (b.latestSeq > lastSeq) {
        // Con eventos esperando en el buffer ya hay un temporizador de hueco
        // en marcha que pedirá el reenvío.
        if (buffered.length > 0) return "pending";
        record?.("gap", lastSeq);
        liveSync(lastSeq);
        return "catchup";
      }
      return "none";
    },

    /** Punto de partida al conectar por primera vez. */
    async baseline() {
      try {
        const data = await fetchSeq();
        if (!disposed) tracker.init(data);
      } catch {
        /* sin punto de partida: el primer evento lo fija */
      }
    },

    /**
     * Pone al día tras una vuelta. Lanza si no se puede contactar al
     * servidor (el orquestador lo trata como "failed").
     * @returns {Promise<{mode: "none"|"replay"|"snapshot", catalog: boolean}>}
     */
    async catchUp() {
      if (disposed) return { mode: "none", catalog: false };
      catchingUp = true;
      snapshotNeeded = snapshotPending;
      snapshotPending = false;
      let catalog = false;
      let replayed = false;
      try {
        const data = (await fetchSeq()) || {};
        const st = tracker.getState();
        if (st.lastSeq == null) {
          // Nunca tuvimos punto de partida: no sabemos qué nos perdimos.
          tracker.init(data);
          return { mode: "snapshot", catalog: false };
        }
        if (st.epoch != null && data.epoch != null && data.epoch !== st.epoch) {
          // API reiniciada: secuencia nueva → estado completo (incl. catálogo).
          tracker.init(data);
          return { mode: "snapshot", catalog: true };
        }
        if (data.latestSeq != null && data.latestSeq < st.lastSeq) {
          tracker.applySync({ snapshotRequired: true, epoch: data.epoch, latestSeq: data.latestSeq });
          return { mode: "snapshot", catalog: true };
        }
        if (data.latestSeq != null && data.latestSeq > st.lastSeq) {
          await doSync(st.lastSeq);
          replayed = true;
        }
        return { mode: snapshotNeeded ? "snapshot" : replayed ? "replay" : "none", catalog };
      } catch (err) {
        // Lo que estuviera pendiente se mantiene para el siguiente intento.
        if (snapshotNeeded) snapshotPending = true;
        throw err;
      } finally {
        catchingUp = false;
        snapshotNeeded = false;
      }
    },

    dispose() {
      disposed = true;
      tracker.dispose();
    },
  };
}

/**
 * 2026-10-05 · Puesta al día al conectar por primera vez. Un hueco solo se
 * detecta cuando llega el evento siguiente: lo emitido entre la línea base y la
 * conexión (p. ej. la comanda que entra justo al abrir la pantalla de cocina)
 * se perdía si no llegaba nada más. `onConnected` es el callback de conexión de
 * CreateConnection (devuelve un thunk): espera a la línea base y hace
 * `catchUp()`; sin novedades cuesta un `realtime/seq`. No fuerza snapshot (la
 * pantalla acaba de cargar sus datos).
 */
export function createConnectCatchUp(sync) {
  let baselineReady = Promise.resolve();
  return {
    baseline() {
      baselineReady = Promise.resolve(sync.baseline());
      return baselineReady;
    },
    onConnected: ({ connectedHub } = {}) => () => {
      if (!connectedHub) return Promise.resolve();
      return baselineReady.then(() => sync.catchUp()).catch(() => {});
    },
  };
}
